// hooks/useSkillsCached.js
import { useCallback, useEffect, useState } from 'react';
import { useCacheManager } from './useCacheManager';

// Utiliser l'URL du User Backend depuis les variables d'environnement
const USER_BACKEND_API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL;

export const useSkillsCached = (domainId = null, options = {}) => {
    // Options par défaut
    const {
        cacheDuration = 5 * 60 * 1000, // 5 minutes
        forceRefresh = false,
        autoRefresh = true
    } = options;

    // États
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastFetch, setLastFetch] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);
    const [filters, setFilters] = useState({
        id_domaine: domainId,
        completed: null,
        min_rating: null,
        max_rating: null,
        actif: true
    });

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache, hasDataChanged } = useCacheManager();

    // Headers pour les requêtes
    const getHeaders = useCallback(() => {
        const token = localStorage.getItem('access_token');
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    }, []);

    // Fonction utilitaire pour gérer les erreurs
    const handleError = useCallback((error, customMessage = 'Une erreur est survenue') => {
        const message = error?.message || customMessage;
        setError(error);
        console.error('❌ Erreur skills:', message);
        return { success: false, error: message };
    }, []);

    // 📋 RÉCUPÉRER LES COMPÉTENCES AVEC CACHE
    const fetchSkills = useCallback(async (customFilters = {}, useCache = true) => {
        // Préparer la clé de cache
        const allFilters = { ...filters, ...customFilters };
        const cleanFilters = {};

        Object.entries(allFilters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== 'null' && value !== '') {
                cleanFilters[key] = typeof value === 'boolean' ? value.toString() : value;
            }
        });

        const cacheKey = getCacheKey('skills', cleanFilters);

        // Vérifier le cache d'abord (sauf si forceRefresh)
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Compétences chargées depuis le cache');
                setSkills(cachedData);
                setIsFromCache(true);
                setError(null);
                return { success: true, skills: cachedData, fromCache: true };
            }
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            const queryParams = new URLSearchParams(cleanFilters);
            const url = `${USER_BACKEND_API_BASE_URL}/skills?${queryParams}`;

            console.log('🌐 Requête API skills:', url);
            const response = await fetch(url, { headers: getHeaders() });

            if (response.ok) {
                const data = await response.json();
                const fetchedSkills = data.skills || [];

                // Sauvegarder dans le cache
                setCacheData(cacheKey, fetchedSkills, cacheDuration);
                
                setSkills(fetchedSkills);
                setLastFetch(Date.now());
                
                console.log(`✅ ${fetchedSkills.length} compétences chargées depuis l'API`);
                return { success: true, skills: fetchedSkills, fromCache: false };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors du chargement des compétences');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la récupération des compétences');
        } finally {
            setLoading(false);
        }
    }, [filters, getCacheKey, getCacheData, setCacheData, getHeaders, handleError, forceRefresh, cacheDuration]);

    // 🆕 CRÉER UNE NOUVELLE COMPÉTENCE avec invalidation du cache
    const createSkill = useCallback(async (skillData) => {
        setLoading(true);
        setError(null);

        try {
            if (!skillData.nom_skill?.trim()) {
                throw new Error('Le nom de la compétence est requis');
            }
            if (!skillData.id_domaine) {
                throw new Error('Le domaine est requis');
            }

            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/skills`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(skillData)
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des compétences
                invalidateCache('skills');
                console.log('🗑️ Cache skills invalidé après création');
                
                // Ajouter à l'état local pour un feedback immédiat
                setSkills(prev => [...prev, data.skill]);
                
                return { success: true, skill: data.skill };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la création de la compétence');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la création de la compétence');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // ✏️ METTRE À JOUR UNE COMPÉTENCE avec invalidation du cache
    const updateSkill = useCallback(async (skillId, updateData) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/skills/${skillId}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des compétences
                invalidateCache('skills');
                console.log('🗑️ Cache skills invalidé après modification');
                
                // Mettre à jour l'état local
                setSkills(prev =>
                    prev.map(skill =>
                        skill.id_skill === skillId
                            ? { ...skill, ...data.skill }
                            : skill
                    )
                );
                
                return { success: true, skill: data.skill };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la mise à jour de la compétence');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la mise à jour de la compétence');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // 🔄 METTRE À JOUR UNE COMPÉTENCE AVEC CHANGEMENT DE NOM (NOUVEAU)
    const updateSkillWithNameChange = useCallback(async (oldSkillName, newSkillData) => {
        setLoading(true);
        setError(null);

        try {
            // Trouver l'ID de la compétence par son nom
            const skill = skills.find(s => s.nom_skill === oldSkillName);
            if (!skill) {
                throw new Error('Compétence non trouvée');
            }

            // Préparer les données pour l'API
            const apiData = {
                nom_skill: newSkillData.name,
                description: newSkillData.description,
                training_link: newSkillData.training_link,
                certification: newSkillData.certification,
                niveau_maitrise_requis: newSkillData.niveau_maitrise_requis
            };

            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/skills/${skill.id_skill}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(apiData)
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des compétences
                invalidateCache('skills');
                console.log('🗑️ Cache skills invalidé après modification du nom');
                
                // Mettre à jour l'état local
                setSkills(prev =>
                    prev.map(s =>
                        s.id_skill === skill.id_skill
                            ? { ...s, ...data.skill }
                            : s
                    )
                );
                
                return { 
                    success: true, 
                    skill: data.skill,
                    oldName: oldSkillName,
                    newName: newSkillData.name
                };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la mise à jour de la compétence');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la mise à jour de la compétence');
        } finally {
            setLoading(false);
        }
    }, [skills, getHeaders, handleError, invalidateCache]);

    // 🗑️ SUPPRIMER UNE COMPÉTENCE avec invalidation du cache
    const deleteSkill = useCallback(async (skillId) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/skills/${skillId}`, {
                method: 'DELETE',
                headers: getHeaders()
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des compétences
                invalidateCache('skills');
                console.log('🗑️ Cache skills invalidé après suppression');
                
                // Retirer de l'état local
                setSkills(prev =>
                    prev.filter(skill => skill.id_skill !== skillId)
                );
                
                return { success: true, message: data.message };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || errorData.message || 'Erreur lors de la suppression de la compétence');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la suppression de la compétence');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleError, invalidateCache]);

    // ⭐ NOTER UNE COMPÉTENCE avec invalidation du cache
    const rateSkill = useCallback(async (skillId, rating) => {
        try {
            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/skills/${skillId}/rate`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ rating })
            });

            if (response.ok) {
                const data = await response.json();
                
                // Invalider le cache des compétences
                invalidateCache('skills');
                console.log('🗑️ Cache skills invalidé après notation');
                
                // Mettre à jour l'état local
                setSkills(prev =>
                    prev.map(skill =>
                        skill.id_skill === skillId
                            ? { ...skill, rating: data.rating }
                            : skill
                    )
                );
                
                return { success: true, rating: data.rating };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la notation');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la notation de la compétence');
        }
    }, [getHeaders, handleError, invalidateCache]);

    // 🔄 RÉORGANISER LES COMPÉTENCES avec invalidation du cache
    const reorderSkills = useCallback(async (domainId, skillIdsOrNames) => {
        try {
            const firstItem = skillIdsOrNames[0];
            let skillIds;
            
            if (typeof firstItem === 'string' && isNaN(firstItem)) {
                skillIds = skillIdsOrNames.map(skillName => {
                    const skill = skills.find(s => s.nom_skill === skillName);
                    return skill ? skill.id_skill : null;
                }).filter(Boolean);
            } else {
                skillIds = skillIdsOrNames.filter(Boolean);
            }

            if (skillIds.length === 0) {
                throw new Error('Aucune compétence valide trouvée pour la réorganisation');
            }

            const response = await fetch(`${USER_BACKEND_API_BASE_URL}/skills/reorder`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({
                    domain_id: domainId,
                    skill_ids: skillIds
                })
            });

            if (response.ok) {
                // Invalider le cache des compétences
                invalidateCache('skills');
                console.log('🗑️ Cache skills invalidé après réorganisation');
                
                // Mettre à jour l'ordre local immédiatement
                setSkills(prev => {
                    const newSkills = [...prev];
                    skillIds.forEach((skillId, index) => {
                        const skillIndex = newSkills.findIndex(s => s.id_skill === skillId);
                        if (skillIndex !== -1) {
                            newSkills[skillIndex] = {
                                ...newSkills[skillIndex],
                                ordre_affichage: index
                            };
                        }
                    });
                    return newSkills.sort((a, b) => (a.ordre_affichage || 0) - (b.ordre_affichage || 0));
                });

                return { success: true };
            } else {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Erreur lors de la réorganisation');
            }
        } catch (error) {
            return handleError(error, 'Erreur lors de la réorganisation des compétences');
        }
    }, [getHeaders, handleError, skills, invalidateCache]);

    // 🔄 RAFRAÎCHIR LES COMPÉTENCES (force le refetch)
    const refreshSkills = useCallback(() => {
        console.log('🔄 Force refresh des compétences');
        return fetchSkills({}, false);
    }, [fetchSkills]);

    // 🎯 REFETCH avec gestion intelligente du cache
    const refetchSkills = useCallback(async () => {
        console.log('🔄 Refetch intelligent des compétences');
        
        // Si les données sont récentes (moins de 1 minute), ne pas refetch
        if (lastFetch && (Date.now() - lastFetch) < 60000 && !forceRefresh) {
            console.log('⏭️ Données récentes, skip refetch');
            return { success: true, skills, fromCache: true };
        }
        
        return fetchSkills({}, false);
    }, [fetchSkills, lastFetch, forceRefresh, skills]);

    // 🎯 COMPÉTENCES PAR DOMAINE avec cache
    const getSkillsByDomain = useCallback(() => {
        const byDomain = {};
        skills.forEach(skill => {
            if (!byDomain[skill.id_domaine]) {
                byDomain[skill.id_domaine] = [];
            }
            byDomain[skill.id_domaine].push(skill);
        });
        return byDomain;
    }, [skills]);

    // 🎯 METTRE À JOUR LES FILTRES
    const updateFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
    }, []);

    // 🔄 CHARGER LES COMPÉTENCES AU MONTAGE
    useEffect(() => {
        if (domainId) {
            setFilters(prev => ({ ...prev, id_domaine: domainId }));
        }
    }, [domainId]);

    useEffect(() => {
        if (autoRefresh) {
            fetchSkills();
        }
    }, [fetchSkills, autoRefresh]);

    // 🧹 NETTOYER LES ERREURS
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // États
        skills,
        loadingSkills: loading,
        errorSkills: error,
        isFromCache,
        lastFetch,
        filters,

        // Actions CRUD
        fetchSkills,
        createSkill,
        updateSkill,
        updateSkillWithNameChange, // NOUVEAU
        deleteSkill,
        rateSkill,
        reorderSkills,

        // Actions spécifiques
        refreshSkills,
        refetchSkills,

        // Filtres et recherche
        updateFilters,

        // Utilitaires
        getSkillsByDomain,
        clearError,
        
        // Cache management
        invalidateCache: () => invalidateCache('skills')
    };
};